/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * MemoryPolicyPanel
 * Compact, read-only view of the agent's memory policy. Intended to sit
 * above the memory browser as the new unified "Memory" tab.
 */

import React from 'react';
import { Box, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import {
  Edit as EditIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';

import type { AgentDetail as AgentDetailType } from '../../../types';
import { SectionCard } from './primitives';

interface MemoryPolicyPanelProps {
  agent: AgentDetailType;
  onEdit: () => void;
}

const PolicyRow: React.FC<{
  label: string;
  value: React.ReactNode;
  helper?: string;
}> = ({ label, value, helper }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.65rem' }}>
      {label}
    </Typography>
    <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
      {value}
    </Typography>
    {helper ? (
      <Typography variant="caption" color="text.secondary">
        {helper}
      </Typography>
    ) : null}
  </Box>
);

export const MemoryPolicyPanel: React.FC<MemoryPolicyPanelProps> = ({ agent, onEdit }) => {
  const c = agent.config || {};
  const shortTerm = c.short_term_capacity ?? 100;
  const longTerm = c.long_term_capacity ?? 1000;
  const autoSummarize = c.auto_summarize !== false;
  const pruning = c.enable_pruning !== false;
  const semantic = c.enable_semantic_search !== false;

  return (
    <SectionCard
      icon={<TuneIcon fontSize="small" />}
      title="Memory policy"
      description="Controls how this agent stores, prunes, and retrieves memories."
      actions={
        <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>
          Edit policy
        </Button>
      }
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <PolicyRow
            label="Short-term"
            value={shortTerm.toLocaleString()}
            helper="Recent turns"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <PolicyRow
            label="Long-term"
            value={longTerm.toLocaleString()}
            helper="Persistent memories"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <PolicyRow
            label="Max iterations"
            value={c.max_iterations ?? 50}
            helper="Per execution"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <PolicyRow
            label="Max tokens"
            value={(c.max_tokens ?? 2000).toLocaleString()}
            helper="Per response"
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={0.75} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={`Auto-summarise: ${autoSummarize ? 'On' : 'Off'}`}
          color={autoSummarize ? 'success' : 'default'}
          variant={autoSummarize ? 'filled' : 'outlined'}
        />
        <Chip
          size="small"
          label={`Pruning: ${pruning ? 'On' : 'Off'}`}
          color={pruning ? 'success' : 'default'}
          variant={pruning ? 'filled' : 'outlined'}
        />
        <Chip
          size="small"
          label={`Semantic search: ${semantic ? 'On' : 'Off'}`}
          color={semantic ? 'success' : 'default'}
          variant={semantic ? 'filled' : 'outlined'}
        />
        {autoSummarize ? (
          <Chip
            size="small"
            variant="outlined"
            label={`Trigger @ ${c.max_context_messages ?? 20} msgs, keep ${c.keep_recent_messages ?? 10}`}
          />
        ) : null}
      </Stack>
    </SectionCard>
  );
};

export default MemoryPolicyPanel;
